import React, { ReactElement, useEffect, useMemo, useState } from "react";
import {Link} from "react-router-dom";
import {motion} from "framer-motion";
import {MotionUtils} from "app/utils/motionUtils";
import {UserMenuModel} from "app/model/ua/user-menu";
import ArcButton, {ButtonType} from "app/components/arc/ArcButton";

const scroll = {
  init: {
    height: 0,
    minHeight: 0,
    opacity: 0,
  },
  down: {
    height: 'auto',
    minHeight: '12rem',
    opacity: 1,
  },
  up: {
    height: 0,
    minHeight: 0,
    opacity: 0,
  }
}

export interface MegaMenuOverlayProps {
  ref?: React.RefObject<HTMLDivElement>;
  visible?: boolean;
}

export interface MegaMenuItemProps {
  menu: UserMenuModel;
  activeMenu?: UserMenuModel | undefined;
  onChangeVisibleState?: (visible: boolean) => void;
  onClick?: (selectedMenu: UserMenuModel, selectedMenusRootMenu: UserMenuModel) => void;
}

export interface MegaMenuProps {
  visible?: boolean;
  menus?: UserMenuModel[] | undefined | null
  activeMenu?: UserMenuModel | undefined
  onClick?: (selectedMenu: UserMenuModel, selectedMenusRootMenu: UserMenuModel) => void;
}

function MegaMenuOverlay(props: Readonly<MegaMenuOverlayProps>): ReactElement {
  const {ref, visible = props.visible ?? false } = props;
  return (
    <motion.div
      variants={MotionUtils.overlay}
      initial="init"
      animate={visible ? 'in' : 'out'}
      transition={{duration: 0.1, ease: 'easeInOut'}}
      className="overlay"
      ref={ref}>
    </motion.div>
  );
}

function MegaMenuItem(props: Readonly<MegaMenuItemProps>): ReactElement {
  const {
    menu,
    activeMenu,
    onChangeVisibleState,
    onClick
  } = props

  const [ visible, setVisible ] = useState(false);
  const [ isMenuClicked, setIsMenuClicked ] = useState(false);
  const [ isMouseEnter, setIsMouseEnter ] = useState(false);
  const [ isMouseOver, setIsMouseOver ] = useState(false);

  function handleClickMenu(clickedMenu: UserMenuModel, rootMenu?: UserMenuModel) {
    if (clickedMenu && onClick) {
      onClick(clickedMenu, rootMenu ? rootMenu : clickedMenu);
    }
    setIsMenuClicked(true);
  }

  const handleMouseEnterMenu = (isMouseEnter: boolean) => {
    setIsMouseEnter(isMouseEnter);
  }

  const handleMouseOverButton = (isMouseOver: boolean) => {
    setIsMouseOver(isMouseOver);
  }

  useEffect(() => {
    if (isMenuClicked) {
      setIsMenuClicked(false);
    }
  }, [isMouseEnter, isMouseOver])

  useEffect(() => {
    const newVisible: boolean = !isMenuClicked && (isMouseEnter || isMouseOver);
    setVisible(newVisible);

    const hasChildrenMenus: boolean = (menu?.subMenus?? []).length > 0;

    if (onChangeVisibleState && hasChildrenMenus) {
      onChangeVisibleState(newVisible);
    }
  }, [isMouseEnter, isMouseOver, isMenuClicked]);

  return (
    <>
      {menu && (
        <li
          className={(activeMenu?.menuId === menu.menuId || visible) ? "on" : ""}
          onMouseEnter={() => handleMouseEnterMenu(true)}
          onFocus={() => handleMouseEnterMenu(true)}
          onMouseLeave={() => handleMouseEnterMenu(false)}
          onBlur={() => handleMouseEnterMenu(false)}>
          <ArcButton
            type={ButtonType.custom}
            isExportAnchorTag={false}
            text={menu.menuNm}
            onClick={() => handleClickMenu(menu)}
            onMouseOver={() => handleMouseOverButton(true)}
            onMouseOut={() => handleMouseOverButton(false)}
          />
          {menu.subMenus && menu.subMenus.length > 0 && (
            <motion.div
              variants={scroll}
              initial="init"
              animate={visible ? 'down' : 'up'}
              transition={{ duration: 0.15, delay: 0.1 }}
              className="depth-wrap"
            >
              <div className="depth-tit">
                <span>{menu?.menuNm}</span>
              </div>
              <motion.div
                  variants={MotionUtils.fade}
                  initial="init"
                  animate={visible ? 'in' : 'out'}
                  transition={{duration: 0.3, ease: 'easeInOut'}}
                  className="w-1600">
                <ul className="depth02-w">
                  {menu?.subMenus?.map((i: UserMenuModel) => {
                    return (
                      <li key={i.menuId}>
                        <Link to="#" onClick={(e: any) => { e.preventDefault(); handleClickMenu(i, menu); }}><span>{i.menuNm}</span></Link>
                        <ul className="depth03-w">
                          {i.subMenus?.map((sub: UserMenuModel) => {
                            return (
                              <li key={sub.menuId}>
                                <Link to="#" onClick={(e: any) => { e.preventDefault(); handleClickMenu(sub, menu); }}><span>{sub.menuNm}</span></Link>
                              </li>
                            );
                          })}
                        </ul>
                      </li>
                    );
                  })}
                </ul>
              </motion.div>
            </motion.div>
          )}
        </li>
      )}
    </>
  );
}

export default function MegaMenu(props: MegaMenuProps) {
  const {
    menus,
    visible = props.visible ?? true,
    activeMenu,
    onClick
  } = props;

  const [ megaMenuVisible, setMegaMenuVisible ] = useState(false);
  const menuMap: { menuId: string, visible: boolean }[] = useMemo(() => {
    const tempMenuMap: { menuId: string, visible: boolean }[] = [];
    if (menus && menus.length > 0) {
      menus.forEach(menu => {
        if (menu.menuId) {
          tempMenuMap.push({
            menuId: menu.menuId,
            visible: false
          });
        }
      });
    }
    return tempMenuMap;
  }, [menus]);

  function handleChangeVisibleStateMegaMenu(menuId: string | undefined, visible: boolean) {
    if (menuMap && menuMap.length > 0) {
      if (menuId) {
        const menu = menuMap.find(menu => menu.menuId === menuId);
        if (menu) {
          menu.visible = visible;
        }
      }
      setMegaMenuVisible(menuMap.filter(menu => menu.visible).length > 0);
    }
  }

  return (
    <>
      {visible && menus && menus.length > 0 && (
        <>
          <MegaMenuOverlay visible={megaMenuVisible} />
          <ul>
            {menus.map((menu: UserMenuModel) => (
              <MegaMenuItem
                key={menu.menuId}
                menu={menu}
                activeMenu={activeMenu}
                onChangeVisibleState={(visible: boolean) => handleChangeVisibleStateMegaMenu(menu.menuId, visible)}
                onClick={onClick}
              />
            ))}
          </ul>
        </>
      )}
    </>
  );
}